import PKCE from 'js-pkce';
import { Base64 } from 'js-base64';
// eslint-disable-next-line import/no-named-as-default
import Cookies from 'cookies-ts';
import { Env } from '@/types/environment';
import { environments } from '@/environment';

/**
 * Hand an unauthenticated user over to the portal's headless `/login` page. It
 * clears any stale token, runs the OAuth flow and sends the user back to
 * `redirect` once signed in.
 */
export function redirectToPortalLogin(env: Env): void {
  const returnUrl = encodeURIComponent(window.location.href);

  window.location.replace(`${environments[env].portalUrl}/login?redirect=${returnUrl}`);
}

/**
 * Send an authenticated-but-forbidden user back to the portal home.
 *
 * Deliberately the portal root and not its `/c360` route: that route is
 * deprecated and redirects straight back here, which would loop.
 */
export function redirectToPortalHome(env: Env): void {
  window.location.replace(environments[env].portalUrl);
}

/**
 * DEAD CODE — no callers remain.
 *
 * @deprecated Superseded by `redirectToPortalLogin`. main.tsx used to call this
 * behind an `isLocalDev()` check; that branch is gone, so nothing reaches it.
 *
 * Kept deliberately for now, but be aware of two things before relying on it:
 *
 *  1. It is stale. `siteUrl` hardcodes port 3000, which is now the portal —
 *     vite.config.ts moved this app to 3001.
 *  2. It is not free. boot.ts imports `redirectToPortalLogin` from this module,
 *     so this file is in the production graph and its top-level `js-pkce` /
 *     `js-base64` imports ship in the bundle. Both are declared in
 *     devDependencies, so `npm audit --omit=dev` does not scan them.
 *
 * Delete it, or move those two packages to `dependencies` to make the situation
 * honest.
 *
 * USED FOR DEVELOPMENT ONLY
 * This is dirty hack to make the dev login work
 **/
export async function handleLogin(handler: 'Request' | 'Callback'): Promise<void> {
  const siteUrl = 'http://localhost:3000';

  const callbackUrl = '/auth/callback';

  const auth = {
    oAuthUrl: 'https://vc-auth.preprod.vestiairecollective.com/realms/backoffice/protocol/openid-connect',
    scopes: 'openid email profile',
  };

  const pkce = new PKCE({
    client_id: 'new-backoffice-portal',
    redirect_uri: `${siteUrl}${callbackUrl}`,
    authorization_endpoint: `${auth.oAuthUrl}/auth`,
    token_endpoint: `${auth.oAuthUrl}/token`,
    requested_scopes: auth.scopes,
  });

  switch (handler) {
    case 'Request': {
      const state = Base64.btoa(
        JSON.stringify({
          env: 'dev',
          nonce: window.crypto.randomUUID(),
        }),
      );

      window.location.replace(pkce.authorizeUrl({ state }));
    }

    case 'Callback': {
      const token = await pkce.exchangeForAccessToken(window.location.toString());

      const cookies = new Cookies();

      localStorage.setItem('access_token', token.access_token);
      cookies.set('access_token', token.access_token, {
        expires: `${token.expires_in}s`,
        domain: 'localhost', // To change according to your setup
      });

      if ('id_token' in token && typeof token.id_token === 'string') {
        localStorage.setItem('id_token', token.id_token);
        cookies.set('id_token', token.id_token, {
          expires: `${token.expires_in}s`,
          domain: 'localhost', // To change according to your setup
        });
      }

      window.location.href = siteUrl;
    }
  }
}
